import { Box } from '@ally/metronome-ui'
import React, {
  useState,
  useContext,
  MutableRefObject,
  useRef,
  memo,
} from 'react'
import { useParams } from 'react-router-dom'
import allytm from '@ally/allytm'
import { SearchPanel } from './SearchPanel'
import { TransactionHistoryHeading } from './TransactionHistoryHeading'
import { TransactionHistoryTable } from './Table/TransactionHistoryTable'
import { SearchForm } from './SearchForm'
import { useSearchTransactionsApi } from '../../../api/search-transactions-api'
import {
  TransactionHistoryContext,
  TransactionHistoryProvider,
} from '../../../services'
import { SearchResults, CheckDetails } from '../../../types'
import { DownloadForm } from './DownloadForm'
import { DownloadPanel } from './DownloadPanel'
import { TransactionHistoryDrawerStyle as Drawer } from '../Drawer'
import { formatDateForApi } from '../../../utils/formatter'
import { useFeatureFlags } from '../../../hooks/use-feature-flags'
import { Flags } from '../../../utils/feature-flags'

export interface DrawerProps {
  children: any
  onClose: () => void
}

interface FormProps {
  fileFormat?: string
  keywords?: string
  dateRange: string
  startDate: string
  toDate: string
}

interface TransactionHistoryProps {
  printCheckCallback: (data: CheckDetails) => void
  headerRef: MutableRefObject<HTMLHeadingElement | null>
}

export const TransactionHistoryContents: React.FC<TransactionHistoryProps> =
  memo(({ printCheckCallback, headerRef }) => {
    const { transactionHistoryState } = useContext(TransactionHistoryContext)
    const { update } = transactionHistoryState
    const { fetchSearchTransactions } = useSearchTransactionsApi()
    const { id } = useParams<{ id: string }>()

    const ffTransactionDownloadApigee = useFeatureFlags(
      Flags.TransactionDownloadApigee,
    )

    // local states
    const [searchResults, setSearchResults] = useState<SearchResults[] | null>(
      null,
    )
    const [searchError, setSearchError] = useState(false)
    const [transactionHistoryDrawer, setTransactionHistoryDrawer] = useState<{
      selected: string
      isHidden: boolean
    }>({ selected: '', isHidden: true })

    const { selected, isHidden } = transactionHistoryDrawer

    const searchButtonRef = useRef<HTMLButtonElement>(null)
    const downloadButtonRef = useRef<HTMLButtonElement>(null)
    /*
     * Setting this value to true allows us to externally trigger
     * a reset in the child form component
     * needed for when the panel is closed or when the account changes
     */
    const [shouldResetForm, setShouldResetForm] = useState(false)

    /* istanbul ignore next */ // TODO - add coverage and remove 'any' type
    const handleSearch = async (values: FormProps): Promise<any> => {
      /* istanbul ignore next */
      allytm.event('customEvent', {
        name: 'searchTransactionHistory',
        type: 'userAttribute',
        attr: {
          user: {
            keyword: values.keywords,
            dateRange: values.dateRange,
            customDateRange: values.dateRange === 'custom' ? 'Y' : 'N',
            customDateRangeStart: values.startDate,
            customDateRangeEnd: values.toDate,
          },
        },
      })
      try {
        setSearchError(false)
        update({ isLoading: true })
        const response = await fetchSearchTransactions(values)
        setSearchResults(response.transaction)
      } catch (error) {
        setSearchResults([])
        // we are already testing we should show no transactions on error
        /* istanbul ignore else */
        if (error instanceof Error) {
          // we are already testing we should show no transactions on error
          /* istanbul ignore else */
          if (error.message === 'USB_BUS_062') {
            setSearchResults([])
          } else {
            allytm.event(
              'customError',
              `We can't load your transaction history at this time. Try again later.`,
              '',
            )
            // we are already testing to show table error if submit throws error
            /* istanbul ignore else */
            setSearchError(true)
          }
        }
      } finally {
        update({ isLoading: false })
      }
    }

    /* istanbul ignore next */
    const handleDownload = async (values: FormProps): Promise<any> => {
      const startDate = formatDateForApi(values.startDate)
      const toDate = formatDateForApi(values.toDate)
      const urlHostname = window.location.hostname
      const capiDownloadPath = `https://${urlHostname}/capi-gw/accounts/${id}/transactions.${values.fileFormat}?fromDate=${startDate}&toDate=${toDate}&status=Posted`
      const apigeeDownloadPath = `https://${urlHostname}/acs/v1/bank-accounts/transactions/${id}/${values.fileFormat}?fromDate=${startDate}&toDate=${toDate}&status=POSTED`
      const downloadPath = ffTransactionDownloadApigee
        ? apigeeDownloadPath
        : capiDownloadPath

      window.open(downloadPath)
      allytm.event('customEvent', {
        name: 'downloadAccountActivity',
        type: 'userAttribute',
        attr: {
          user: {
            fileFormat: values.fileFormat,

            dateRange: values.dateRange,

            customDateRange: values.dateRange === 'custom' ? 'Y' : 'N',

            customDateRangeStart: values.startDate,
            customDateRangeEnd: values.toDate,
          },
        },
      })
    }

    const toggleDrawer = (isOpen: boolean): void => {
      setTransactionHistoryDrawer(prevState => ({
        ...prevState,
        isHidden: !isOpen,
      }))
    }

    // TODO - test case where drawer is closed while download is selected
    /* istanbul ignore next */
    const onClose = (): void => {
      if (selected === 'search') {
        /* istanbul ignore next */
        searchButtonRef.current?.focus()
        allytm.event('clickEvent', {
          tagName: 'span',
          allytmln: `transactionHistory-search-close`,
        })
      }
      if (selected === 'download') {
        /* istanbul ignore next */
        downloadButtonRef.current?.focus()
        allytm.event('clickEvent', {
          tagName: 'span',
          allytmln: `transactionHistory-download-close`,
        })
      }
      toggleDrawer(false)
      setSearchResults(null)
      setSearchError(false)
      setShouldResetForm(true)
    }

    const selectedTransactionHistorySelection = (): any => {
      if (selected === 'search') {
        return (
          <SearchPanel onClose={onClose}>
            <SearchForm
              handleSubmit={handleSearch}
              shouldResetForm={shouldResetForm}
              setShouldResetForm={setShouldResetForm}
            />
          </SearchPanel>
        )
      }
      if (selected === 'download') {
        return (
          <DownloadPanel onClose={onClose}>
            <DownloadForm
              handleSubmit={handleDownload}
              shouldResetForm={shouldResetForm}
              setShouldResetForm={setShouldResetForm}
            />
          </DownloadPanel>
        )
      }
      return <></>
    }

    return (
      <Box mt="lg" as="section">
        <Drawer
          control={
            <TransactionHistoryHeading
              onClose={onClose}
              headerRef={headerRef}
              transactionHistoryDrawer={transactionHistoryDrawer}
              setTransactionHistoryDrawer={setTransactionHistoryDrawer}
            />
          }
          isHidden={isHidden}
          onClose={onClose}
        >
          {selectedTransactionHistorySelection()}
        </Drawer>
        <TransactionHistoryTable
          searchResults={searchResults}
          searchError={searchError}
          printCheckCallback={printCheckCallback}
        />
      </Box>
    )
  })

export const TransactionHistory: React.FC<TransactionHistoryProps> = ({
  printCheckCallback,
  headerRef,
}) => (
  <TransactionHistoryProvider>
    <TransactionHistoryContents
      printCheckCallback={printCheckCallback}
      headerRef={headerRef}
    />
  </TransactionHistoryProvider>
)
